import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  asChild?: boolean;
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

// Hover styles are gated behind `can-hover` (hover-capable + fine pointer) so the
// same React tree on iOS/Android touch doesn't get stuck-hover that needs a
// second tap to clear. Active/disabled states stay unconditional.
const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-t1 text-canvas can-hover:hover:bg-t1-hover active:bg-t1-hover disabled:bg-t3 disabled:text-canvas',
  secondary:
    'bg-card text-t2 border border-line-2 can-hover:hover:border-line-3 can-hover:hover:text-t1 active:bg-rail disabled:bg-rail disabled:text-t3',
  ghost: 'bg-transparent text-t2 can-hover:hover:bg-rail can-hover:hover:text-t1 disabled:text-t3',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-2xs gap-1.5 rounded-md pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px]',
  md: 'h-9 px-3 text-caption gap-2 rounded-lg pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px]',
  lg: 'h-11 px-4 text-body-lg gap-2 rounded-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    asChild = false,
    loading = false,
    disabled,
    className,
    children,
    leadingIcon,
    trailingIcon,
    ...rest
  },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  const isDisabled = Boolean(disabled) || loading;

  return (
    <Comp
      ref={ref}
      data-variant={variant}
      data-size={size}
      disabled={isDisabled}
      aria-busy={loading ? true : undefined}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap font-medium leading-none',
        'pressable focus-visible:outline-none',
        'focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
        'disabled:cursor-not-allowed',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <span className="inline-flex shrink-0">
          <Spinner />
        </span>
      ) : leadingIcon ? (
        <span className="inline-flex shrink-0">{leadingIcon}</span>
      ) : null}
      <Slottable>{children}</Slottable>
      {trailingIcon ? <span className="inline-flex shrink-0">{trailingIcon}</span> : null}
    </Comp>
  );
});
